import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import CloudEvalAnalysis from '../view_web_eval/CloudEvalAnalysis';
import { cloudEvalPath } from '../view_web_eval/cloudEvalRoutes';
import { SKIP_UNSAVED_GUARD } from '../shared/UnsavedChangesGuard';
import {
  EVAL_REVIEWS_CHANGED_EVENT,
  loadReportQueue,
  markEvalScored,
  reportQueuePath,
} from './reportQueueApi';

async function loadEvalView(evaluationId) {
  const db = getFirestore();
  const evaluationDoc = await getDoc(doc(db, 'cloud_eval_results', evaluationId));
  if (!evaluationDoc.exists()) return null;
  const evaluation = { id: evaluationDoc.id, ...evaluationDoc.data() };

  const clientDoc = evaluation.client ? await getDoc(doc(db, 'clients', evaluation.client)) : null;
  const client = clientDoc?.exists() ? { id: clientDoc.id, ...clientDoc.data() } : null;

  let masterResults = null;
  if (client) {
    const sex = client.Sex === 0 ? 'male' : 'female';
    const masterDoc = await getDoc(doc(db, 'master_results', sex));
    masterResults = masterDoc.exists() ? masterDoc.data() : null;
  }

  return { evaluation, client, masterResults };
}

export const MARK_DONE_UNSAVED_MESSAGE = 'This report has unsaved changes. Mark it as done without saving them?';

/**
 * One eval in the report queue: queue controls and a "Mark as Done" button
 * above the regular cloud eval view (scoring form + Report tab), like the
 * stimulus label queue.
 */
export default function ReportQueueEval() {
  const { evaluationId } = useParams();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState('');
  const [reportDirty, setReportDirty] = useState(false);
  const handleReportDirtyChange = useCallback((dirty) => setReportDirty(dirty), []);

  useEffect(() => {
    let active = true;
    setQueueLoading(true);
    loadReportQueue()
      .then((items) => { if (active) setQueue(items); })
      .catch((err) => console.error('Error loading report queue:', err))
      .finally(() => { if (active) setQueueLoading(false); });
    return () => { active = false; };
  }, [evaluationId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setMarkError('');
    setReportDirty(false);
    loadEvalView(evaluationId)
      .then((loaded) => {
        if (!active) return;
        if (!loaded) setError('Evaluation not found');
        setView(loaded);
      })
      .catch((err) => {
        console.error('Error loading evaluation:', err);
        if (active) setError('Failed to load evaluation');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [evaluationId]);

  const queueIndex = queue.findIndex((item) => item.evaluationId === evaluationId);
  const queueItem = queueIndex >= 0 ? queue[queueIndex] : null;

  const goToIndex = (index) => {
    if (index < 0 || index >= queue.length) return;
    navigate(reportQueuePath(queue[index].evaluationId));
  };

  const handleMarkDone = async () => {
    if (reportDirty && !window.confirm(MARK_DONE_UNSAVED_MESSAGE)) return;
    setMarking(true);
    setMarkError('');
    try {
      // The backend looks up client, org and case number and takes the uid
      // from the ID token.
      await markEvalScored({ evaluationId });
      window.dispatchEvent(new Event(EVAL_REVIEWS_CHANGED_EVENT));
      const remaining = queue.filter((item) => item.evaluationId !== evaluationId);
      const next = queueIndex >= 0 ? remaining[queueIndex] ?? remaining[queueIndex - 1] : remaining[0];
      // Already confirmed above, so the unsaved-changes guard lets this through.
      navigate(reportQueuePath(next?.evaluationId), { state: { [SKIP_UNSAVED_GUARD]: true } });
    } catch (err) {
      console.error('Failed to mark eval scored:', err);
      setMarkError(err.message || 'Could not mark this eval as done.');
    } finally {
      setMarking(false);
    }
  };

  let queueStatus;
  if (queueLoading) {
    queueStatus = 'Refreshing queue...';
  } else if (queueIndex >= 0) {
    queueStatus = `Eval ${queueIndex + 1} of ${queue.length}`;
  } else {
    queueStatus = `This eval is already marked done. Remaining in queue: ${queue.length}`;
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate(reportQueuePath())}
        className="inline-flex items-center gap-x-1.5 text-sm font-semibold text-gray-900"
      >
        <ArrowLeftIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
        Back to Report Queue
      </button>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-emerald-900">Report Queue</p>
            <p className="text-sm text-emerald-800">{queueStatus}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToIndex(queueIndex - 1)}
              disabled={queueLoading || queueIndex <= 0}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => goToIndex(queueIndex < 0 ? 0 : queueIndex + 1)}
              disabled={queueLoading || queueIndex >= queue.length - 1}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
        {queueIndex >= 0 && (
          <button
            type="button"
            onClick={handleMarkDone}
            disabled={marking || loading || Boolean(error)}
            className="mt-4 flex w-full items-center justify-center gap-x-2 rounded-lg bg-emerald-600 px-6 py-4 text-lg font-bold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircleIcon className="h-7 w-7" aria-hidden="true" />
            {marking ? 'Saving...' : 'Mark as Done'}
          </button>
        )}
        {markError && (
          <p role="alert" className="mt-2 text-sm font-medium text-red-700">{markError}</p>
        )}
      </div>

      {loading && (
        <div className="flex min-h-[240px] items-center justify-center" role="status">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
          <span className="sr-only">Loading evaluation</span>
        </div>
      )}
      {!loading && error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}
      {!loading && !error && view && (
        <>
          {view.client?.organization_id && (
            <a
              href={cloudEvalPath({
                organizationId: view.client.organization_id,
                clientId: view.client.id,
                evaluationId: view.evaluation.id,
              })}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              Open in Cloud Evals
            </a>
          )}
          <CloudEvalAnalysis
            key={view.evaluation.id}
            client={view.client}
            evaluation={view.evaluation}
            masterResults={view.masterResults}
            onReportDirtyChange={handleReportDirtyChange}
          />
        </>
      )}
    </div>
  );
}
